import React, { useState } from "react";
import { Link } from "react-router-dom";
import type { MemberSearchFirm } from "../../../Services/MemberSearch";
import { formatMemberSearchFirmLocation } from "../../../utils/memberSearchLocationFormat";
import { buildFirmPath } from "../../../utils/memberSearchNavigation";
import MemberSearchKeywordMatchHint from "./MemberSearchKeywordMatchHint";
import "../../../pages/AboutUs/AboutUs.css";

type MemberSearchFirmCardProps = {
  firm: MemberSearchFirm;
  keyword?: string;
};

const formatFirmServiceType = (serviceType?: string) => {
  if (!serviceType || serviceType === "Other") {
    return "Legal Services";
  }
  return serviceType;
};

const formatFirmLocationDisplay = (firm: MemberSearchFirm) => {
  const fromFields = formatMemberSearchFirmLocation(
    firm.cityName,
    firm.stateName,
    firm.countryName,
  );
  if (fromFields) {
    return fromFields;
  }

  const subtitle = firm.subtitle?.trim();
  if (!subtitle) {
    return "";
  }
  return subtitle.replace(/\s*\|\s*/g, ", ");
};

const MemberSearchFirmCard: React.FC<MemberSearchFirmCardProps> = ({
  firm,
  keyword = "",
}) => {
  const [logoError, setLogoError] = useState(false);
  const logo = firm.smallLogo || firm.logo;
  const firmPath = buildFirmPath(firm);
  const locationLabel = formatFirmLocationDisplay(firm);

  return (
    <div className="relative flex h-full flex-col py-2 hover:z-20">
      <div className="flex items-start gap-6 md:gap-8">
        <div className="flex h-[117px] w-[192px] shrink-0 items-center justify-center border border-gray-200 bg-white p-3">
          {logo && !logoError ? (
            <img
              src={logo}
              alt=""
              className="max-h-full max-w-full object-contain"
              onError={() => setLogoError(true)}
            />
          ) : (
            <span className="text-center text-base font-bold uppercase text-gray-font md:text-lg">
              {firm.name.slice(0, 2)}
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1 space-y-1">
          {firmPath ? (
            <Link
              to={firmPath}
              className="council-firm block text-left !text-[20px] !font-bold uppercase leading-snug transition-colors hover:text-red-600 hover:underline"
            >
              {firm.name}
            </Link>
          ) : (
            <p className="council-firm m-0 text-left !text-[20px] !font-bold uppercase leading-snug">
              {firm.name}
            </p>
          )}

          <div className="flex flex-col gap-0">
            <p className="council-services m-0 text-left text-base leading-none md:text-lg">
              {formatFirmServiceType(firm.serviceType)}
            </p>

            {locationLabel ? (
              <p className="council-services m-0 -mt-0.5 text-left text-base leading-none md:text-lg">
                {locationLabel}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {firm.hiddenKeywordMatch && keyword.trim() ? (
        <div className="mt-auto w-full pt-3">
          <MemberSearchKeywordMatchHint
            match={firm.hiddenKeywordMatch}
            keyword={keyword}
          />
        </div>
      ) : null}
    </div>
  );
};

export default MemberSearchFirmCard;
